<template>
	<Teleport to="body">
		<div v-if="open" class="cta-modal-overlay" @click.self="emit('dismiss')">
			<div class="cta-modal">
				<div v-if="!plain" class="photo-header">
					<img :src="landerzMarkUri" class="brand-mark" alt="Landerz" />
					<h3>{{ title }}</h3>
				</div>
				<div class="body">
					<h3 v-if="plain" class="plain-title">{{ title }}</h3>
					<p>{{ message }}</p>
					<ul class="features">
						<li v-for="feature in features" :key="feature.key">
							<span class="icon"><AppIcon :name="feature.icon" :size="14" /></span>
							{{ feature.label }}
						</li>
					</ul>
					<div class="actions">
						<Button variant="primary" class="confirm" size="m" :disabled="confirmDisabled" @click="emit('confirm')">
							{{ confirmLabel }}
						</Button>
						<Button v-if="dismissLabel" variant="ghost" size="m" class="dismiss" @click="emit('dismiss')">
							{{ dismissLabel }}
						</Button>
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import Button from "@/components/ui/Button.vue"
import AppIcon from "@/components/AppIcon.vue"
import type { IconName } from "@/components/icons/registry"
import { buildLanderzMarkDataUri } from "@/utils/opportunityIcons"

type CtaModalFeature = {
	key: string
	label: string
	icon: IconName
}

defineProps<{
	open: boolean
	title: string
	message: string
	features: CtaModalFeature[]
	confirmLabel: string
	dismissLabel?: string
	confirmDisabled?: boolean
	plain?: boolean
}>()

const emit = defineEmits<{ confirm: []; dismiss: [] }>()

const landerzMarkUri = buildLanderzMarkDataUri()
</script>

<style scoped>
.cta-modal-overlay {
	position: fixed;
	inset: 0;
	background-color: var(--overlay-backdrop);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.cta-modal {
	background-color: var(--neutral-0);
	border-radius: var(--border-radius-lg);
	max-width: 400px;
	width: 90%;
	overflow: hidden;
	box-shadow: var(--shadow-xl);
	text-align: center;

	.photo-header {
		position: relative;
		height: var(--space-120);
		background-image: linear-gradient(180deg, rgb(6 32 22 / 15%) 0%, rgb(6 32 22 / 75%) 100%),
			url("@/assets/images/terrain-stock.jpg");
		background-size: cover;
		background-position: center 65%;
	}

	.brand-mark {
		position: absolute;
		left: var(--spacing-lg);
		bottom: calc(var(--space-24) * -1);
		width: var(--space-48);
		height: var(--space-48);
		object-fit: contain;
		filter: drop-shadow(0 4px 10px rgb(0 0 0 / 28%));
	}

	.body {
		padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-lg);
		display: flex;
		flex-direction: column;
		align-items: stretch;
		gap: var(--spacing-smd);
		text-align: left;
	}

	.photo-header h3 {
		position: absolute;
		left: calc(var(--spacing-lg) + var(--space-10));
		right: var(--spacing-lg);
		bottom: calc(var(--space-24) + var(--spacing-md));
		margin: 0;
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-bold);
		color: var(--neutral-0);
		text-align: left;
		text-shadow: 0 1px 4px rgb(0 0 0 / 40%);
	}

	.plain-title {
		margin: 0;
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-bold);
		color: var(--neutral-900);
	}

	p {
		margin: 0;
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-medium);
		line-height: var(--line-height-relaxed);
		color: var(--neutral-800);
		text-wrap: pretty;
	}

	.features {
		margin: 0;
		padding: var(--spacing-smd) var(--spacing-md);
		list-style: none;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		border: var(--border-width-thin) solid var(--neutral-100);
		border-radius: var(--border-radius-md);
		background: var(--neutral-50);
		font-size: var(--font-size-small);
		color: var(--neutral-800);

		li {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
		}

		.icon {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: var(--space-24);
			height: var(--space-24);
			border-radius: var(--border-radius-sm);
			background-color: color-mix(in srgb, var(--primary-green) 10%, var(--neutral-0));
			color: var(--primary-green);
		}
	}

	.actions {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--spacing-smd);
		width: 100%;
		padding-top: var(--spacing-sm);

		.confirm {
			width: 100%;
			min-width: auto;
		}

		.dismiss {
			color: var(--neutral-600);
		}
	}

	@media (max-width: 640px) {
		width: calc(100% - 2 * var(--spacing-xl));

		.photo-header {
			height: var(--space-100);
		}

		.brand-mark {
			left: var(--spacing-md);
		}

		.photo-header h3 {
			left: calc(var(--spacing-md) + var(--space-10));
			right: var(--spacing-md);
			font-size: var(--font-size-medium);
		}

		.body {
			padding: var(--spacing-xl) var(--spacing-md) var(--spacing-md);
		}
	}
}
</style>
